// src/domain/utilities/utilityFeatureTypes.ts

// High-level discipline
export type UtilityDisciplineId = "DrainageAndUtilities";

// ORD topology under that discipline
export type UtilityTopology = "Area" | "Link" | "Node";

// Path that mirrors the ORD tree:
// Drainage and Utilities > Node > ElectricalNode > Lighting
// Drainage and Utilities > Link > ElectricalSegment > Lighting
export interface UtilityCategoryPath {
  discipline: UtilityDisciplineId;   // "DrainageAndUtilities"
  topology: UtilityTopology;         // "Node"
  nodeKind?: string;                 // "ElectricalNode", "GasNode", ...
  group?: string;                    // "Lighting", "Resource", "Signal", ...
}

// One concrete, selectable utility feature type in our app
export interface UtilityFeatureType {
  /** Stable ID used everywhere in the app (Selection tree, queries, export) */
  id: string;                        // e.g. "util.node.elec.light.equipmentPadB"
  label: string;                     // e.g. "Equipment Pad B"
  path: UtilityCategoryPath;
  /** ORD DefinitionName used in FeatureWithNameAspect, etc. */
  definitionName: string;            // e.g. "Node\\ElectricalNode\\Lighting\\Equipment Pad B"
  /** Optional future hooks (IFC mapping, pay item hints, etc.) */
  ifcType?: string;
}

// For this contract we only populate:
// Drainage and Utilities > Node > ElectricalNode > Lighting
// Drainage and Utilities > Link > ElectricalSegment > Lighting
export const utilityFeatureTypes: UtilityFeatureType[] = [
  // Equipment Pad B
  {
    id: "util.node.elec.light.equipmentPadB",
    label: "Equipment Pad B",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Node",
      nodeKind: "ElectricalNode",
      group: "Lighting",
    },
    definitionName: "Node\\ElectricalNode\\Lighting\\Equipment Pad B",
    // ifcType: "IfcSlab", // example placeholder, decide later
  },

  // HandHole - DHVT
  {
    id: "util.node.elec.light.handholeDhvt",
    label: "HandHole-DHVT",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Node",
      nodeKind: "ElectricalNode",
      group: "Lighting",
    },
    definitionName: "Node\\ElectricalNode\\Lighting\\HandHole-DHVT",
  },

  // HandHole - NDHVT
  {
    id: "util.node.elec.light.handholeNdhvt",
    label: "HandHole-NDHVT",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Node",
      nodeKind: "ElectricalNode",
      group: "Lighting",
    },
    definitionName: "Node\\ElectricalNode\\Lighting\\HandHole-NDHVT",
  },

  // Light Unit Type 9-40
  {
    id: "util.node.elec.light.unitType9_40",
    label: "Light Unit Type 9-40",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Node",
      nodeKind: "ElectricalNode",
      group: "Lighting",
    },
    definitionName: "Node\\ElectricalNode\\Lighting\\Light Unit Type 9-40",
    ifcType: "IfcLightFixture", // likely, but we can finalize later
  },

  // Light Unit Type 9-49 
  {
    id: "util.node.elec.light.unitType9_49",
    label: "Light Unit Type 9-49",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Node",
      nodeKind: "ElectricalNode",
      group: "Lighting",
    },
    definitionName: "Node\\ElectricalNode\\Lighting\\Light Unit Type 9-49",
    ifcType: "IfcLightFixture", // likely, but we can finalize later
  },

  // Light Unit Type Spec1
  {
    id: "util.node.elec.light.unitTypeSpec1",
    label: "Light Unit Type Spec1",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Node",
      nodeKind: "ElectricalNode",
      group: "Lighting",
    },
    definitionName: "Node\\ElectricalNode\\Lighting\\Light Unit Type Spec1",
    ifcType: "IfcLightFixture", // likely, but we can finalize later
  },

  // Non-Metallic Conduit (Lighting) - Link
  {
    id: "util.link.elec.light.nonMetallicConduit",
    label: "Non-Metallic Conduit (Lighting)",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Link",
      nodeKind: "ElectricalSegment",
      group: "Lighting",
    },
    definitionName:
      "Link\\ElectricalSegment\\Lighting\\Non-Metallic Conduit (Lighting)",
  },

  // Non-Metallic Conduit (Signal) - Link
  {
    id: "util.link.elec.signal.nonMetallicConduit",
    label: "Non-Metallic Conduit (Signal)",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Link",
      nodeKind: "ElectricalSegment",
      group: "Signal",
    },
    definitionName:
      "Link\\ElectricalSegment\\Signal\\Non-Metallic Conduit (Signal)",
  },

  // Signal - Handhole-NDHVT
  {
    id: "util.node.elec.signal.handholeNdhvt",
    label: "Handhole-NDHVT",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Node",
      nodeKind: "ElectricalNode",
      group: "Signal",
    },
    definitionName: "Node\\ElectricalNode\\Signal\\Handhole-NDHVT",
  },

  // Signal - Ped Push Button
  {
    id: "util.node.elec.signal.pedPushButton",
    label: "Ped Push Button",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Node",
      nodeKind: "ElectricalNode",
      group: "Signal",
    },
    definitionName: "Node\\ElectricalNode\\Signal\\Ped Push Button",
  },

  // Signal - Equipment Pad
  {
    id: "util.node.elec.signal.equipmentPad",
    label: "Signal Equipment Pad",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Node",
      nodeKind: "ElectricalNode",
      group: "Signal",
    },
    definitionName: "Node\\ElectricalNode\\Signal\\Signal Equipment Pad",
  },

  // Signal - PA100-A-40
  {
    id: "util.node.elec.signal.pa100A40",
    label: "Signal PA100-A-40",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Node",
      nodeKind: "ElectricalNode",
      group: "Signal",
    },
    definitionName: "Node\\ElectricalNode\\Signal\\Signal PA100-A-40",
  },

  // Signal - Service Cabinet
  {
    id: "util.node.elec.signal.serviceCabinet",
    label: "Signal Service Cabinet",
    path: {
      discipline: "DrainageAndUtilities",
      topology: "Node",
      nodeKind: "ElectricalNode",
      group: "Signal",
    },
    definitionName: "Node\\ElectricalNode\\Signal\\Signal Service Cabinet",
  },
];

